// src/hooks/workspace/useWorkspace.ts
import { useMemo } from "react";
import {
  useWorkspaceStore,
  selectFocus,
  selectTimeframe,
  selectBenchmarks,
  selectRecentFocuses,
  selectIsAssistantPanelOpen,
  selectAssistantPanelWidth,
  selectAssistantPrefill,
} from "@/stores/workspaceStore";

/**
 * Main workspace hook - returns core context (focus + timeframe + benchmarks).
 * Use this when you need the full workspace context.
 */
export function useWorkspace() {
  const focus = useWorkspaceStore(selectFocus);
  const timeframe = useWorkspaceStore(selectTimeframe);
  const benchmarks = useWorkspaceStore(selectBenchmarks);

  return useMemo(
    () => ({ focus, timeframe, benchmarks }),
    [focus, timeframe, benchmarks]
  );
}

/**
 * Returns just the current focus (token, sector, etc. being viewed).
 */
export function useWorkspaceFocus() {
  return useWorkspaceStore(selectFocus);
}

/**
 * Returns just the current timeframe setting.
 */
export function useWorkspaceTimeframe() {
  return useWorkspaceStore(selectTimeframe);
}

/**
 * Returns benchmark settings (primary, secondary, enabled).
 */
export function useWorkspaceBenchmarks() {
  return useWorkspaceStore(selectBenchmarks);
}

/**
 * Returns recent focus history.
 */
export function useRecentFocuses() {
  return useWorkspaceStore(selectRecentFocuses);
}

/**
 * Returns stable action references for modifying workspace state.
 * Actions are stable across renders, so this is safe to use in dependencies.
 */
export function useWorkspaceActions() {
  const setFocus = useWorkspaceStore((s) => s.setFocus);
  const setComparisonFocus = useWorkspaceStore((s) => s.setComparisonFocus);
  const clearFocus = useWorkspaceStore((s) => s.clearFocus);
  const setTimeframe = useWorkspaceStore((s) => s.setTimeframe);
  const setCustomTimeframe = useWorkspaceStore((s) => s.setCustomTimeframe);
  const setBenchmarks = useWorkspaceStore((s) => s.setBenchmarks);
  const toggleBenchmarks = useWorkspaceStore((s) => s.toggleBenchmarks);

  return useMemo(
    () => ({
      setFocus,
      setComparisonFocus,
      clearFocus,
      setTimeframe,
      setCustomTimeframe,
      setBenchmarks,
      toggleBenchmarks,
    }),
    [
      setFocus,
      setComparisonFocus,
      clearFocus,
      setTimeframe,
      setCustomTimeframe,
      setBenchmarks,
      toggleBenchmarks,
    ]
  );
}

/**
 * Returns assistant panel state and actions.
 */
export function useAssistantPanel() {
  const isOpen = useWorkspaceStore(selectIsAssistantPanelOpen);
  const width = useWorkspaceStore(selectAssistantPanelWidth);
  const prefill = useWorkspaceStore(selectAssistantPrefill);
  const toggle = useWorkspaceStore((s) => s.toggleAssistantPanel);
  const setOpen = useWorkspaceStore((s) => s.setAssistantPanelOpen);
  const setWidth = useWorkspaceStore((s) => s.setAssistantPanelWidth);
  const setPrefill = useWorkspaceStore((s) => s.setAssistantPrefill);
  const clearPrefill = useWorkspaceStore((s) => s.clearAssistantPrefill);
  const openWithPrompt = useWorkspaceStore((s) => s.openAssistantWithPrompt);

  return useMemo(
    () => ({
      isOpen,
      width,
      prefill,
      toggle,
      setOpen,
      setWidth,
      setPrefill,
      clearPrefill,
      openWithPrompt,
    }),
    [isOpen, width, prefill, toggle, setOpen, setWidth, setPrefill, clearPrefill, openWithPrompt]
  );
}

/**
 * Utility hook to get a formatted context string for the AI assistant.
 * Returns a string like "Viewing SOL (token)" or null if no focus.
 */
export function useWorkspaceContextString(): string | null {
  const focus = useWorkspaceStore(selectFocus);
  const timeframe = useWorkspaceStore(selectTimeframe);

  return useMemo(() => {
    if (!focus.type || !focus.entityId) {
      return null;
    }

    const entityLabel = focus.entityName ?? focus.entityId;
    const typeLabel = focus.type === "token" ? "token" : focus.type === "sector" ? "sector" : focus.type;

    return `Currently viewing ${entityLabel} (${typeLabel}) over ${timeframe.preset}`;
  }, [focus, timeframe]);
}

/**
 * Helper to check if there's an active focus.
 */
export function useHasFocus(): boolean {
  const focus = useWorkspaceStore(selectFocus);
  return focus.type !== null && focus.entityId !== null;
}
